/**
 * Query + mutation hooks for Case Notes (#692) — the LF-internal record of work
 * on a matter.
 *
 *   useCaseNotes(leadId, enabled) → GET  /api/case/:leadId/notes
 *   useAddCaseNote(leadId)        → POST /api/placements/:placementId/notes
 *
 * The read is matter-scoped and the write is placement-scoped, which is not an
 * inconsistency: a note about a handling firm anchors to the specific placement
 * it concerns, but the panel showing the case wants every placement's notes at
 * once — including placements that were declined, because why the last two
 * firms fell through is exactly what the operator is looking for.
 *
 * Key space: ["case-notes", leadId]. The mutation invalidates its matter's key
 * only; notes on one case never appear on another.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CaseNotesForLeadResponseSchema,
  FirmCaseNotesResponseSchema,
  type AddCaseNoteRequest,
  type CaseNote,
  type CaseNotesForLeadResponse,
  type FirmCaseNotesResponse,
} from "@command-center/shared";
import { apiGet, apiPost } from "./api";

const caseKey = (leadId: string) => ["case-notes", leadId] as const;

/** A matter's notes plus the placements a new one can be written against.
 *  Fetched only while the section is rendered (`enabled`). */
export function useCaseNotes(leadId: string, enabled: boolean) {
  return useQuery<CaseNotesForLeadResponse, Error>({
    queryKey: caseKey(leadId),
    enabled,
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        `/api/case/${encodeURIComponent(leadId)}/notes`,
      );
      const parsed = CaseNotesForLeadResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error("case-notes: response shape mismatch");
      }
      return parsed.data;
    },
  });
}

/**
 * Where a note is being written. `hf` carries the placement it concerns; the
 * other three carry nothing extra, because they anchor to the matter itself.
 * The union makes the invalid combination — an `hf` note with no placement —
 * unrepresentable rather than merely discouraged.
 */
export type AddCaseNoteVars =
  | (AddCaseNoteRequest & { about: "hf"; placementId: string })
  | (AddCaseNoteRequest & { about: "of" | "client" | "lf" });

/**
 * Append a note. Invalidates the matter's notes so the timeline re-reads.
 *
 * No optimistic insert: a note is a permanent, append-only record, and showing
 * one that later turns out not to have been written would be a lie about the
 * case's history — the one thing this feature exists to prevent.
 */
export function useAddCaseNote(leadId: string) {
  const qc = useQueryClient();
  return useMutation<CaseNote, Error, AddCaseNoteVars>({
    mutationFn: async (vars) => {
      // The write path follows what the note is about, exactly as the storage
      // rule does — `anchorFor` in shared is the same decision on the server.
      if (vars.about === "hf") {
        const { placementId, about: _about, ...body } = vars;
        return apiPost<CaseNote>(
          `/api/placements/${encodeURIComponent(placementId)}/notes`,
          body,
        );
      }
      return apiPost<CaseNote>(
        `/api/case/${encodeURIComponent(leadId)}/notes`,
        vars,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: caseKey(leadId) });
    },
  });
}

/**
 * A firm's notes — both capacities, split.
 *
 * A SEPARATE read from the firm page, not a field on it. Firm Success is a pure
 * computed snapshot: every value it returns is derived and stamped
 * `computedThrough`. Notes are the other kind of thing — authored, unaggregated
 * and live. Putting them behind that date would describe them wrongly, and
 * folding them into the book would mean loading the whole notes table on every
 * firm-page render.
 */
export function useFirmCaseNotes(firmId: string, enabled: boolean) {
  return useQuery<FirmCaseNotesResponse, Error>({
    queryKey: ["case-notes", "firm", firmId],
    enabled,
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        `/api/firm-success/firm/${encodeURIComponent(firmId)}/notes`,
      );
      const parsed = FirmCaseNotesResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error("firm case-notes: response shape mismatch");
      }
      return parsed.data;
    },
  });
}
